/**
 * Per-call capacity fallback for models explicitly routed to Vertex global.
 * Remaining attempts use regional gemini-2.5-pro after a capacity failure;
 * the next logical call still tries its configured model. This includes
 * Gemini 3 and the explicitly admitted gemini-4-argon, not unknown 4.x IDs.
 */
import { GLOBAL_ONLY_MODEL } from './geminiLocations';

/** GA fallback on a capacity error (2nd entry of MODEL_PREFERENCES). */
export const GEMINI_CAPACITY_FALLBACK_MODEL = 'gemini-2.5-pro';

/** 429 / RESOURCE_EXHAUSTED / 503-style overload, as it appears in SDK error text. */
const CAPACITY_ERROR = /\b429\b|resource[\s_]exhausted|\b503\b|high demand|overloaded|\bunavailable\b/i;

/** True for an explicitly globally routed model with a capacity failure. */
export function shouldUseCapacityFallback(model: string, errorMessage: string): boolean {
  return GLOBAL_ONLY_MODEL.test(model) && CAPACITY_ERROR.test(errorMessage);
}

/** One configured global-model call served by a fallback model. */
export interface ModelFallbackEntry {
  /** Telemetry stage or audit call site, e.g. 'generateReportContent', 'absence-assertion-judge'. */
  callSite: string;
  requested: string;
  served: string;
  reason: string;
}

/** Per-report fallback summary. Written only when at least one fallback happened. */
export interface ModelFallbackSummary {
  count: number;
  entries: ModelFallbackEntry[];
}

export function toModelFallbackSummary(
  entries: ReadonlyArray<ModelFallbackEntry>,
): ModelFallbackSummary | undefined {
  return entries.length > 0 ? { count: entries.length, entries: [...entries] } : undefined;
}

/**
 * Derive fallbacks from a report's per-attempt call records
 * (generationTelemetry.calls, in completion order). Only active when a
 * configured model is explicitly routed to global. Successful calls served
 * outside that configured family are entries:
 * - after a failed attempt of the same call, it is a withRetry fallback
 *   (capacity → 2.5, or 404 → next preference);
 * - otherwise the fallback was chosen up front (an earlier unavailable-model
 *   result, or a call site with a pinned model).
 * Historical 3.x reason text is retained; Argon receipts name the actual IDs.
 *
 * "Same call" is the row's `callId` (one id per logical call, shared by all
 * its attempts). Rows without one (recorded before callId shipped) pair by
 * stage, which is exact only while calls at a stage never overlap: two
 * overlapping calls at one stage (a withTimeout-abandoned call still retrying
 * while its caller's retry runs) would hand one call's failure to the other's
 * success and swap their reasons. The COUNT is the same either way.
 */
export function deriveModelFallbacks(
  calls: ReadonlyArray<{ stage: string; model: string; success: boolean; callId?: string; failureReason?: 'capacity' | 'timeout' }>,
  configuredModels: ReadonlyArray<string>,
): ModelFallbackEntry[] {
  const configuredGlobalModel = configuredModels.find(m => GLOBAL_ONLY_MODEL.test(m));
  if (!configuredGlobalModel) return [];
  // Prefixed so a callId can never collide with a stage name.
  const pairingKey = (call: { stage: string; callId?: string }) =>
    call.callId ? `call:${call.callId}` : `stage:${call.stage}`;
  const failedByCall = new Map<string, { model: string; reason?: 'capacity' | 'timeout' }>();
  const entries: ModelFallbackEntry[] = [];
  for (const call of calls) {
    const isGlobalModel = GLOBAL_ONLY_MODEL.test(call.model);
    const key = pairingKey(call);
    if (!call.success) {
      if (isGlobalModel) failedByCall.set(key, { model: call.model, reason: call.failureReason });
      continue;
    }
    const failed = failedByCall.get(key);
    failedByCall.delete(key);
    if (isGlobalModel) continue;
    const requested = failed?.model ?? configuredGlobalModel;
    const legacy3xReason = /^gemini-3(?:\.\d+)?-/i.test(requested);
    entries.push({
      callSite: call.stage,
      requested,
      served: call.model,
      reason: failed?.reason
        ? `intentional editorial ${failed.reason} fallback to ${call.model} after a failed ${failed.model} attempt`
        : failed
        ? legacy3xReason
          ? 'retried on a non-3.x model after a failed 3.x attempt'
          : `retried on ${call.model} after a failed ${requested} attempt`
        : legacy3xReason
          ? 'served by a non-3.x model without a failed 3.x attempt at this stage '
            + '(3.x marked unavailable earlier in the run, or the call site pins the model)'
          : `served by ${call.model} without a failed ${requested} attempt at this stage `
            + '(configured model marked unavailable earlier in the run, or the call site pins the model)',
    });
  }
  return entries;
}
